// Project Library — the team-shared list of Solution Studio projects (§3).
//
// Any authenticated engineer can open/continue any project (creator tracked via JWT). Create supports
// two seeds: upload source docs (greenfield) or import an existing Connect instance (brownfield, §4A).
// Step 1 wires the greenfield "name it" create + the listing; doc upload / instance import land in
// later steps.

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Title, Text, Button, Group, Card, Stack, Badge, Table, Modal, TextInput,
  MultiSelect, Loader, Center, Alert, Box, UnstyledButton, Menu, ActionIcon,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconPlus, IconArrowLeft, IconFolder, IconLock, IconAlertCircle,
  IconChevronUp, IconChevronDown, IconSelector, IconDots, IconTrash,
} from '@tabler/icons-react';

import { useAuth } from '../../../shared/contexts/AuthContext';
import {
  listProjects, createProject, deleteProject as deleteProjectApi, isSolutionStudioBackendConfigured,
} from './api_scripts/solnStudioClient';

const PHASE_COLORS = { DESIGN: 'orange', BUILD: 'blue', TEST: 'green' };
const CHANNEL_OPTIONS = ['voice', 'chat', 'sms', 'whatsapp'];
const LANGUAGE_OPTIONS = ['en', 'es', 'fr', 'pt'];

function ProjectLibrary() {
  const navigate = useNavigate();
  const { getApiToken } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [reversed, setReversed] = useState(true); // createdAt desc by default (newest first)

  const configured = isSolutionStudioBackendConfigured();

  const sorted = useMemo(() => sortProjects(projects, sortBy, reversed), [projects, sortBy, reversed]);

  const setSort = (field) => {
    if (field === sortBy) {
      setReversed((r) => !r);
    } else {
      setSortBy(field);
      // Dates feel natural newest-first; text columns A→Z first.
      setReversed(field === 'createdAt');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setProjects(await listProjects(getApiToken));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getApiToken]);

  useEffect(() => {
    if (configured) load();
    else setLoading(false);
  }, [configured, load]);

  return (
    <Container size="lg" py="xl">
      <Group justify="space-between" mb="md">
        <Button variant="subtle" color="orange" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/')}>
          AWS Tools
        </Button>
        <Button
          color="orange"
          leftSection={<IconPlus size={16} />}
          onClick={() => setCreateOpen(true)}
          disabled={!configured}
        >
          New Project
        </Button>
      </Group>

      <Stack gap="xs" mb="lg">
        <Title order={1}>Solution Studio</Title>
        <Text c="dimmed">
          Design, build, and test Amazon Connect solutions. Projects are team-shared — open any to continue.
        </Text>
      </Stack>

      {!configured && (
        <Alert color="yellow" icon={<IconAlertCircle size={16} />} title="Backend not configured">
          <code>REACT_APP_SOLUTION_STUDIO_API_BASE_URL</code> is not set. Deploy the Solution Studio API and
          rebuild to enable project creation.
        </Alert>
      )}

      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} title="Couldn't load projects" mb="md">
          {error}
        </Alert>
      )}

      {loading ? (
        <Center py="xl"><Loader color="orange" /></Center>
      ) : projects.length === 0 ? (
        <Card withBorder padding="xl" radius="md">
          <Center>
            <Stack align="center" gap="sm">
              <IconFolder size={48} color="#FF9900" />
              <Text c="dimmed">No projects yet.</Text>
              {configured && (
                <Button color="orange" variant="light" leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
                  Create your first project
                </Button>
              )}
            </Stack>
          </Center>
        </Card>
      ) : (
        <Table.ScrollContainer minWidth={680}>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <SortableTh field="name" sortBy={sortBy} reversed={reversed} onSort={setSort}>Project Name</SortableTh>
                <SortableTh field="customer" sortBy={sortBy} reversed={reversed} onSort={setSort}>Customer</SortableTh>
                <SortableTh field="status" sortBy={sortBy} reversed={reversed} onSort={setSort}>Status</SortableTh>
                <SortableTh field="phase" sortBy={sortBy} reversed={reversed} onSort={setSort}>Phase</SortableTh>
                <SortableTh field="createdAt" sortBy={sortBy} reversed={reversed} onSort={setSort}>Created</SortableTh>
                <Table.Th w={48} aria-label="Actions" />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sorted.map((p) => (
                <ProjectRow
                  key={p.projectId}
                  project={p}
                  onOpen={() => navigate(p.projectId)}
                  onRequestDelete={setDeleteTarget}
                />
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <CreateProjectModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(proj) => { setCreateOpen(false); navigate(proj.projectId); }}
        getApiToken={getApiToken}
      />

      {deleteTarget && (
        <DeleteProjectModal
          project={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={(name) => {
            setDeleteTarget(null);
            notifications.show({ title: 'Project deleted', message: `"${name}" and its artifacts were removed.`, color: 'green' });
            load();
          }}
          getApiToken={getApiToken}
        />
      )}
    </Container>
  );
}

// Sort the lightweight project rows. Dates compare numerically; everything else case-insensitively.
function sortProjects(projects, sortBy, reversed) {
  const dir = reversed ? -1 : 1;
  return [...projects].sort((a, b) => {
    let cmp;
    if (sortBy === 'createdAt') {
      cmp = (a.createdAt || 0) - (b.createdAt || 0);
    } else {
      cmp = String(a[sortBy] ?? '').localeCompare(String(b[sortBy] ?? ''), undefined, { sensitivity: 'base' });
    }
    return cmp * dir;
  });
}

function SortableTh({ field, sortBy, reversed, onSort, children }) {
  const active = sortBy === field;
  const Icon = active ? (reversed ? IconChevronDown : IconChevronUp) : IconSelector;
  return (
    <Table.Th>
      <UnstyledButton onClick={() => onSort(field)} style={{ width: '100%' }}>
        <Group gap={4} wrap="nowrap">
          <Text size="sm" fw={600}>{children}</Text>
          <Icon size={14} color={active ? undefined : '#adb5bd'} />
        </Group>
      </UnstyledButton>
    </Table.Th>
  );
}

function ProjectRow({ project, onOpen, onRequestDelete }) {
  const locked = project.lockedBy && (!project.lockExpiresAt || project.lockExpiresAt > Date.now());
  const created = project.createdAt ? new Date(project.createdAt).toLocaleDateString() : '—';
  return (
    <Table.Tr style={{ cursor: 'pointer' }} onClick={onOpen}>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Text fw={500} lineClamp={1}>{project.name}</Text>
          {locked && <IconLock size={14} color="#fa5252" title={`Generating (${project.lockedBy})`} />}
          {project.origin === 'instance-export' && <Badge size="xs" variant="outline" color="grape">brownfield</Badge>}
        </Group>
      </Table.Td>
      <Table.Td><Text size="sm" c="dimmed" lineClamp={1}>{project.customer}</Text></Table.Td>
      <Table.Td><Badge size="sm" variant="outline" color="gray">{project.status}</Badge></Table.Td>
      <Table.Td><Badge size="sm" color={PHASE_COLORS[project.phase] || 'gray'} variant="light">{project.phase}</Badge></Table.Td>
      <Table.Td><Text size="sm">{created}</Text></Table.Td>
      <Table.Td onClick={(e) => e.stopPropagation()}>
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray" aria-label="Project actions" onClick={(e) => e.stopPropagation()}>
              <IconDots size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={14} />}
              disabled={locked}
              onClick={() => onRequestDelete(project)}
            >
              Delete project
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Table.Td>
    </Table.Tr>
  );
}

function CreateProjectModal({ opened, onClose, onCreated, getApiToken }) {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [customer, setCustomer] = useState('');
  const [channels, setChannels] = useState(['voice']);
  const [languages, setLanguages] = useState(['en']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Send a human-readable creator so the workspace shows a name, not the Azure AD oid.
      const createdByName = user?.name || user?.email;
      const proj = await createProject(
        { name, customer: customer || name, channels, languages, createdByName },
        getApiToken,
      );
      onCreated(proj);
      setName(''); setCustomer(''); setChannels(['voice']); setLanguages(['en']);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="New Solution Studio project" centered>
      <Stack>
        {error && <Alert color="red" icon={<IconAlertCircle size={16} />}>{error}</Alert>}
        <TextInput label="Project name" placeholder="e.g. Acme Connect IVR" value={name} onChange={(e) => setName(e.currentTarget.value)} required />
        <TextInput label="Customer" placeholder="Customer / account name" value={customer} onChange={(e) => setCustomer(e.currentTarget.value)} />
        <MultiSelect label="Channels" data={CHANNEL_OPTIONS} value={channels} onChange={setChannels} />
        <MultiSelect label="Languages" data={LANGUAGE_OPTIONS} value={languages} onChange={setLanguages} />
        <Box>
          <Text size="xs" c="dimmed">
            Source-document upload and existing-instance import (brownfield) are added in the next steps.
          </Text>
        </Box>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button color="orange" loading={submitting} disabled={!name} onClick={submit}>Create</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// Confirmation gate for the irreversible project delete: the engineer must type the exact project
// name before the Delete button enables. On success the backend removes the row + purges all S3 storage.
function DeleteProjectModal({ project, onClose, onDeleted, getApiToken }) {
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const confirmed = confirmText.trim() === project.name;

  const submit = async () => {
    if (!confirmed) return;
    setSubmitting(true);
    setError(null);
    try {
      await deleteProjectApi(project.projectId, project.version, getApiToken);
      onDeleted(project.name);
    } catch (err) {
      setError(
        err.reason === 'locked'
          ? 'A generation job is running — deletes are paused until it finishes.'
          : err.reason === 'stale-version'
          ? 'This project changed since the page loaded. Refresh and try again.'
          : err.message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened onClose={onClose} title="Delete project" centered>
      <Stack>
        {error && <Alert color="red" icon={<IconAlertCircle size={16} />}>{error}</Alert>}
        <Alert color="red" icon={<IconAlertCircle size={16} />} title="This cannot be undone">
          Deleting <strong>{project.name}</strong> permanently removes the project and all of its backend
          artifacts (design model, documents, flows, build &amp; test outputs). Resources already imported
          into a Connect instance are not affected.
        </Alert>
        <TextInput
          label={`Type the project name to confirm`}
          placeholder={project.name}
          value={confirmText}
          onChange={(e) => setConfirmText(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && confirmed) submit(); }}
          autoFocus
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button color="red" loading={submitting} disabled={!confirmed} onClick={submit}>Delete</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default ProjectLibrary;
